// src/resources/Letters/LetterCreate.js
import React, { useState, useRef, useEffect } from "react";
import { Create, SimpleForm, useNotify, useRedirect } from "react-admin";
import { onSnapshot, collection } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { generateReference, saveLetter } from "./letterUtils";
import { handleDownloadPDF } from "./pdfHelper";
import LetterFormFields from "./LetterFormFields";
import LetterPreview from "./LetterPreview";

export const LetterCreate = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [letterheadUrl, setLetterheadUrl] = useState("");
  const [type, setType] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [direction, setDirection] = useState("ltr");
  const [generating, setGenerating] = useState(false);
  const [date] = useState(new Date().toISOString());
  const previewRef = useRef();
  const notify = useNotify();
  const redirect = useRedirect();

  // 🔹 Load companies
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  // 🔹 Update letterhead when company changes
  useEffect(() => {
    const company = companies.find((c) => c.id === selectedCompany);
    setLetterheadUrl(company?.letterheadUrl || "");
  }, [selectedCompany, companies]);

  // 🔹 Auto-generate reference number when company & type selected
  useEffect(() => {
    const generateAutoRef = async () => {
      if (selectedCompany && type && !referenceNo && companies.length > 0) {
        const company = companies.find((c) => c.id === selectedCompany);
        if (company) {
          const refNo = await generateReference(company, type, setGenerating, notify);
          setReferenceNo(refNo);
        }
      }
    };
    generateAutoRef();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, type, companies]);

  // 🔹 Save Letter (Preserve alignment & styling)
  const handleSave = async () => {
    try {
      const company = companies.find((c) => c.id === selectedCompany);
      if (!company)
        return notify("Please select a company.", { type: "warning" });
      if (!type.trim())
        return notify("Please select or enter a letter type.", {
          type: "warning",
        });

      const refNo =
        referenceNo ||
        (await generateReference(company, type, setGenerating, notify));
      setReferenceNo(refNo);

      // ✅ Preserve alignment, direction, and font styling even after saving
const cleanBody = body
  // 1️⃣ Convert Quill alignment classes into inline CSS
  .replace(/class="ql-align-center"/g, 'style="text-align:center;"')
  .replace(/class="ql-align-right"/g, 'style="text-align:right;"')
  .replace(/class="ql-align-justify"/g, 'style="text-align:justify;"')
  .replace(/class="ql-align-left"/g, 'style="text-align:left;"')
  // 2️⃣ Add consistent paragraph styling (without overwriting existing inline styles)
  .replace(
    /<p(?![^>]*style=)([^>]*)>/g,
    `<p$1 style="font-family:${
      direction === "rtl"
        ? "'Noto Naskh Arabic','Amiri',serif"
        : "Calibri, Arial, sans-serif"
    }; color:#000; line-height:1.8; margin:0 0 8px 0; direction:${direction}; text-align:${
      direction === "rtl" ? "right" : "justify"
    };">`
  )
  // 3️⃣ Keep spans readable with slight spacing
  .replace(/<span(?![^>]*style=)([^>]*)>/g, `<span$1 style="display:inline-block;">`);


      await saveLetter(company, refNo, {
        type,
        subject,
        body: cleanBody,
        direction,
        date,
      });

      notify("✅ Letter created successfully!");
      redirect("/letters");
    } catch (err) {
      console.error("❌ Save failed:", err);
      notify("❌ Failed to save letter.", { type: "error" });
    }
  };

  // 🔹 PDF Download (no page refresh)
  const handlePDF = (e) => {
    e.preventDefault(); // ✅ prevent page reload from SimpleForm
    handleDownloadPDF({
      direction,
      letterheadUrl,
      previewRef,
      subject,
      referenceNo,
      date,
      body,
    });
  };

  return (
    <Create title="Create New Letter">
      <SimpleForm toolbar={false}>
        <LetterFormFields
          companies={companies}
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          type={type}
          setType={setType}
          subject={subject}
          setSubject={setSubject}
          direction={direction}
          setDirection={setDirection}
          body={body}
          setBody={setBody}
          generating={generating}
          referenceNo={referenceNo}
          date={date}
        />

        {letterheadUrl && (
          <LetterPreview
            ref={previewRef}
            letterheadUrl={letterheadUrl}
            direction={direction}
            referenceNo={referenceNo}
            subject={subject}
            body={body}
            date={date}
          />
        )}

        <div
          style={{
            textAlign: "center",
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={handleSave}
            disabled={generating}
            style={{
              background: "#1976d2",
              color: "#fff",
              padding: "10px 22px",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              cursor: "pointer",
              fontWeight: 600,
              opacity: generating ? 0.7 : 1,
            }}
          >
            {generating ? "Generating Ref..." : "💾 Save Letter"}
          </button>

          <button
            type="button"
            onClick={handlePDF}
            style={{
              background: "#2e7d32",
              color: "#fff",
              padding: "10px 22px",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            📥 Download Draft PDF
          </button>
        </div>
      </SimpleForm>
    </Create>
  );
};
